import { useLayoutEffect, useRef, useState, type ImgHTMLAttributes, type SyntheticEvent } from 'react';

/**
 * A photo that arrives whole.
 *
 * Every picture used to paint itself in from the top down over a flat grey
 * box, a band at a time as the bytes came, which on a phone reads as the app
 * struggling. Now a soft pulse holds its place -- or the photo's own blurred
 * preview, where it has one -- until the whole thing is downloaded and
 * decoded, and then it comes in at once.
 *
 * Still the one <img>, kept in the page from the start, so lazy loading, every
 * screen's own CSS for its pictures, and the retry after a refused one
 * (mediaRetry) all work on it exactly as before. While it waits its picture is
 * drawn far outside its own box, which leaves only the background showing.
 */
type Props = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  src: string;
  /** A tiny stand-in, already blurred by being tiny, shown while it comes. */
  preview?: string;
};

type Arrival = 'waiting' | 'arriving' | 'here';

export function Photo({ src, preview, className, style, onLoad, loading = 'lazy', ...rest }: Props) {
  const ref = useRef<HTMLImageElement>(null);
  const [arrival, setArrival] = useState<Arrival>('waiting');

  // Before the first paint: one already on the phone is simply shown, with no
  // placeholder blinking past and no entrance.
  useLayoutEffect(() => {
    const img = ref.current;
    setArrival(img?.complete && img.naturalWidth > 0 ? 'here' : 'waiting');
  }, [src]);

  const loaded = (event: SyntheticEvent<HTMLImageElement>) => {
    onLoad?.(event);
    const img = event.currentTarget;
    const was = img.currentSrc || img.src;
    // Decoded off the main thread first, so it shows in one frame rather than
    // a blank one and then the picture.
    void (typeof img.decode === 'function' ? img.decode() : Promise.resolve())
      .catch(() => undefined)
      .then(() => {
        if ((img.currentSrc || img.src) !== was) return;
        setArrival((now) => (now === 'waiting' ? 'arriving' : now));
      });
  };

  const classes = [
    className,
    arrival === 'waiting' && 'photo-wait',
    arrival === 'waiting' && preview && 'photo-wait--preview',
    arrival === 'arriving' && (preview ? 'photo-sharpen' : 'photo-in'),
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <img
      ref={ref}
      src={src}
      loading={loading}
      decoding="async"
      className={classes || undefined}
      style={arrival === 'waiting' && preview ? { ...style, backgroundImage: `url("${preview}")` } : style}
      onLoad={loaded}
      {...rest}
    />
  );
}
